import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  FilePicker,
  Flex,
  ModalAction,
  ModalBody,
  ModalFooter,
  ModalFooterActions,
  ModalHeader,
  ModalV2,
  Text,
} from '@electricjs/arc';

import { useGlobalUI } from '@/components/GlobalUIProvider';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { useGeneratePresignedUploadUrlsMutation } from '@/redux/slices/assetApiSlice';

enum FileStatus {
  Empty = 'empty',
  InvalidContent = 'invalidContent',
  Undefined = 'undefined',
  Valid = 'valid',
}

const FILE_SAMPLE_SIZE_BYTES = 16 * 1024; // 16 KiB

type AssetsImageUploadModalProps = {
  assetId: string;
  onCancel: () => void;
  onSuccess: () => void;
};

const AssetsImageUploadModal = ({
  assetId,
  onCancel,
  onSuccess,
}: AssetsImageUploadModalProps) => {
  const { t } = useTranslation('assets');
  const organizationId = useGetOrganizationId();
  const [generatePresignedUploadUrls, { isLoading: isGeneratingUrls }] =
    useGeneratePresignedUploadUrlsMutation();
  const [files, setFiles] = useState<File[]>([]);
  const { showSuccessToast, showErrorToast, showToast } = useGlobalUI();
  const [fileStatus, setFileStatus] = useState<FileStatus>(
    FileStatus.Undefined
  );
  const [fileErrorMessage, setFileErrorMessage] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  const handleFileChange = (file: File) => {
    setFileErrorMessage('');
    setFileStatus(FileStatus.Valid);
    setFiles([file]);
  };

  const discardFileContent = () => {
    setFiles([]);
    setFileStatus(FileStatus.Undefined);
    setFileErrorMessage('');
  };

  const readFileSample = async (file: File): Promise<File> => {
    const sampleSize = Math.min(FILE_SAMPLE_SIZE_BYTES, file.size);
    const blob = file.slice(0, sampleSize);
    return new File([blob], file.name, { type: file.type });
  };

  const uploadToS3 = async (
    file: File,
    presignedUrl: string
  ): Promise<void> => {
    try {
      const response = await fetch(presignedUrl, {
        method: 'PUT',
        body: file,
      });

      if (!response.ok) {
        const errorText = await response
          .text()
          .catch(() => response.statusText);
        throw new Error(
          `Failed to upload ${file.name} to S3: ${errorText} (${response.status})`
        );
      }
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(`Failed to upload ${file.name} to S3`);
    }
  };

  const handleSubmit = async (): Promise<void> => {
    if (files.length === 0) {
      showErrorToast({
        message: t('imageUploadModal.selectImageError'),
        id: 'image-missing-error-toast',
      });
      return;
    }

    try {
      setIsUploading(true);

      const fileSamples: File[] = [];
      const fileNames: string[] = [];
      const fileSizes: number[] = [];
      const contentTypes: string[] = [];

      for (const file of files) {
        const sample = await readFileSample(file);
        fileSamples.push(sample);
        fileNames.push(file.name);
        fileSizes.push(file.size);
        contentTypes.push(file.type || 'application/octet-stream');
      }

      const response = await generatePresignedUploadUrls({
        organizationId,
        assetId,
        fileSamples,
        fileNames,
        fileSizes,
        contentTypes,
      }).unwrap();

      const { errors, presignedUrls } = response;

      // Upload files to S3
      const uploadPromises = presignedUrls.map(async presignedInfo => {
        const file = files.find(f => f.name === presignedInfo.filename);
        if (!file) {
          throw new Error(`File not found: ${presignedInfo.filename}`);
        }
        await uploadToS3(file, presignedInfo.presignedUrl);
      });

      try {
        await Promise.all(uploadPromises);
      } catch (uploadError) {
        showErrorToast({
          id: 'asset-image-upload-s3-error-toast',
          message: t('imageUploadModal.uploadError'),
        });
        return;
      }

      if (errors.length > 0) {
        const failedFiles = errors
          .map(error => `${error.filename}: ${error.error}`)
          .join(', ');
        const successfulFiles = presignedUrls
          .map(url => url.filename)
          .join(', ');

        if (failedFiles.length > 0 && successfulFiles.length === 0) {
          showErrorToast({
            id: 'asset-image-upload-file-error-toast',
            message: `${t('imageUploadModal.uploadAllFailed')}\n${t('imageUploadModal.failed')}: ${failedFiles}.`,
          });
        } else {
          showToast({
            id: 'asset-image-upload-info-toast',
            message: `${t('imageUploadModal.uploadPartialSuccess')}\n${t('imageUploadModal.failed')}: ${failedFiles}. ${t('imageUploadModal.successful')}: ${successfulFiles}.`,
          });
          onSuccess();
        }
      } else {
        showSuccessToast({
          id: 'asset-image-upload-success-toast',
          message: t('imageUploadModal.uploadSuccess'),
        });
        onSuccess();
      }
    } catch (error: unknown) {
      let errorMessage = t('imageUploadModal.uploadError');

      if (
        typeof error === 'object' &&
        error !== null &&
        ('data' in error || 'message' in error)
      ) {
        const errorObj = error as {
          data?: { detail?: string; message?: string };
          message?: string;
        };
        errorMessage =
          errorObj.data?.detail ||
          errorObj.data?.message ||
          errorObj.message ||
          errorMessage;
      }

      showErrorToast({
        id: 'asset-image-upload-error-toast',
        message: errorMessage,
      });
      return Promise.reject(error);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <ModalV2 hide={onCancel} visible ariaLabelledby="assets-image-add-modal">
      <ModalHeader showCloseButton={true}>
        <Flex alignItems="center" columnGap="1rem">
          <Text id="assets-image-modal-header-text" variant="heading-2">
            {t('imageUploadModal.title')}
          </Text>
        </Flex>
      </ModalHeader>
      <form>
        <ModalBody>
          <FilePicker
            onChange={handleFileChange}
            onRemove={discardFileContent}
            acceptedExtensions=".jpg,.jpeg,.png,.tiff,.tga,.gif,.bmp,.tif"
            errorMessage={fileErrorMessage}
            dragAndDropText={t('imageUploadModal.filePicker.dragAndDropText')}
            uploadButtonText={t('imageUploadModal.filePicker.uploadButtonText')}
            changeFileButtonText={t(
              'imageUploadModal.filePicker.changeFileButtonText'
            )}
            cancelButtonText={t('imageUploadModal.filePicker.cancelButtonText')}
            fileSizeExceedsMaxErrorText={t(
              'imageUploadModal.filePicker.fileSizeExceedsMaxErrorText'
            )}
            fileReadErrorText={t(
              'imageUploadModal.filePicker.fileReadErrorText'
            )}
            invalidFileTypeErrorText={t(
              'imageUploadModal.filePicker.invalidFileTypeErrorText'
            )}
            formatAcceptedFileTypesText={extensions =>
              t('imageUploadModal.filePicker.formatAcceptedFileTypesText', {
                extensions,
              })
            }
          />
        </ModalBody>
        <ModalFooter>
          <ModalFooterActions>
            <ModalAction id="asset-upload-image-cancel-button" variant="text">
              {t('imageUploadModal.cancel')}
            </ModalAction>
            <ModalAction
              id="assets-modal-add-image-button"
              type="button"
              disabled={fileStatus !== FileStatus.Valid}
              loading={isGeneratingUrls || isUploading}
              onClick={handleSubmit}>
              {t('imageUploadModal.saveImage')}
            </ModalAction>
          </ModalFooterActions>
        </ModalFooter>
      </form>
    </ModalV2>
  );
};

export default AssetsImageUploadModal;
